import { EmptyState } from "@/design-system/components/ui";
import { AccountReviewRequestsTab } from "@/components/portalAbsorption/AccountReviewRequestsTab";
import { AccountNotificationsPanel } from "@/components/portalAbsorption/AccountNotificationsPanel";
import type { BrokerageDetailDto } from "@/api/brokerages";
import { useBrokeragePortalAccount } from "@/hooks/brokerages/useBrokeragePortalAccount";

// Portal absorption (WS-D1) — the "Review & requests" + "Notifications"
// tabs on the Brokerage detail page, thin mounts of the shared
// portal-absorption components scoped by the firm's linked portal account.

function useResolvedPortalUserId(brokerageId: string) {
  const account = useBrokeragePortalAccount(brokerageId);
  return {
    isLoading: account.isLoading,
    portalUserId: account.data?.portalUserId ?? null,
  };
}

const NO_ACCOUNT_DESCRIPTION =
  "This firm has no portal account yet — send the portal invitation from the Portal account tab first.";

export function BrokeragePortalReviewTab({ brokerage }: { brokerage: BrokerageDetailDto }) {
  const { isLoading, portalUserId } = useResolvedPortalUserId(brokerage.id);

  if (isLoading) {
    return <p className="text-caption text-gray-mid">Loading portal account…</p>;
  }
  if (!portalUserId) {
    return <EmptyState title="No portal account" description={NO_ACCOUNT_DESCRIPTION} />;
  }
  return (
    <AccountReviewRequestsTab
      scope={{ portalUserId }}
      requestComposer={{
        accountType: "Brokerage",
        email: brokerage.email,
        // C1 — the firm's own record is the created package's owner (the
        // review-time filing default).
        owner: { ownerEntityType: "Brokerage", ownerEntityId: brokerage.id },
      }}
      emptyMessage="Nothing has been submitted by this firm yet."
    />
  );
}

export function BrokeragePortalNotificationsTab({ brokerage }: { brokerage: BrokerageDetailDto }) {
  const { isLoading, portalUserId } = useResolvedPortalUserId(brokerage.id);

  if (isLoading) {
    return <p className="text-caption text-gray-mid">Loading portal account…</p>;
  }
  if (!portalUserId) {
    return <EmptyState title="No portal account" description={NO_ACCOUNT_DESCRIPTION} />;
  }
  return <AccountNotificationsPanel portalUserId={portalUserId} />;
}
